import {
  BACKGROUND_COLORS,
  EYE_COLORS,
  type FaceShapeId,
  FUR_COLORS,
  type WhiskerId,
} from "./parts.ts";
import type { Avatar } from "./schema.ts";

/**
 * 猫アバターの描画。
 *
 * 出力は SVG 要素の木 (`SvgNode`) で返す。web は Solid の JSX として描き、
 * CLI や静的書き出しは `avatarSvg()` で文字列にする。
 * こうしておくと図形の定義が 1 箇所で済み、innerHTML も要らない。
 */

export type SvgNode = {
  tag: "circle" | "ellipse" | "path" | "rect" | "line" | "g" | "clipPath" | "defs";
  attrs: Record<string, string | number>;
  children?: SvgNode[];
};

export type AvatarDrawing = {
  viewBox: string;
  nodes: SvgNode[];
};

const VIEW_BOX_SIZE = 64;

/** 小数を丸めて SVG のパスを短くする。 */
const r = (value: number): number => Math.round(value * 100) / 100;

type Head = { cx: number; cy: number; rx: number; ry: number };
type Fur = { base: string; shade: string };

const HEADS: Record<FaceShapeId, Head> = {
  round: { cx: 32, cy: 33, rx: 19, ry: 17.5 },
  slim: { cx: 32, cy: 33, rx: 16.5, ry: 19 },
  chubby: { cx: 32, cy: 34, rx: 21, ry: 17 },
};

/** 耳の内側・鼻・口の中など、毛色に依存させない固定色。 */
const SKIN = "#e79ba1";
const SKIN_DARK = "#c2727c";
const MOUTH_IN = "#a85562";
const INK = "#33333a";
const HIGHLIGHT = "#ffffff";

/** アクセサリの色。毛色と衝突しないよう固定にする。 */
const ACCENT = {
  scarf: "#e0575b",
  scarfDark: "#b8444a",
  collar: "#5b8def",
  bell: "#f0c33c",
  bellDark: "#b8912a",
  metal: "#8c93a3",
  gear: "#4a4f5c",
  lens: "#ffffff",
  petal: "#f2a1c0",
  petalDark: "#d97ea3",
  pollen: "#f6d365",
  leaf: "#7fb46b",
  cap: "#5b8def",
  capBrim: "#3f66c8",
  beanie: "#4a4e58",
  beanieCuff: "#c9ced6",
  ribbon: "#e0575b",
  ribbonDark: "#b8444a",
};

const stroke = (
  d: string,
  color: string,
  width: number,
  extra?: Record<string, string | number>,
): SvgNode => ({
  tag: "path",
  attrs: {
    d,
    fill: "none",
    stroke: color,
    "stroke-width": width,
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    ...extra,
  },
});

const fill = (d: string, color: string, extra?: Record<string, string | number>): SvgNode => ({
  tag: "path",
  attrs: { d, fill: color, ...extra },
});

const circle = (
  cx: number,
  cy: number,
  radius: number,
  color: string,
  extra?: Record<string, string | number>,
): SvgNode => ({
  tag: "circle",
  attrs: { cx: r(cx), cy: r(cy), r: r(radius), fill: color, ...extra },
});

const ellipse = (
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  color: string,
  extra?: Record<string, string | number>,
): SvgNode => ({
  tag: "ellipse",
  attrs: { cx: r(cx), cy: r(cy), rx: r(rx), ry: r(ry), fill: color, ...extra },
});

const rect = (
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  color: string,
  extra?: Record<string, string | number>,
): SvgNode => ({
  tag: "rect",
  attrs: {
    x: r(x),
    y: r(y),
    width: r(width),
    height: r(height),
    rx: r(radius),
    fill: color,
    ...extra,
  },
});

/** 左右対称に描くための、head.cx からの外向き距離。side は -1 (左) / 1 (右)。 */
const mirror = (head: Head, side: -1 | 1) => (outward: number) => r(head.cx + side * outward);

/** アクセサリの取り付け位置。描画と外接矩形の計算で同じ値を使う。 */
function anchors(head: Head) {
  const { cx, cy, rx, ry } = head;
  return {
    /** 顎に少しかぶる位置。首輪やマフラーはここを基準にする。 */
    neckY: cy + ry * 0.86,
    eyeY: cy + ry * 0.02,
    eyeDx: rx * 0.44,
    /** 帽子のつばの高さ。 */
    brimY: cy - ry * 0.66,
    /** リボンの結び目。 */
    knot: { x: cx + rx * 0.72, y: cy - ry * 0.95 },
    /** 花の中心。 */
    flower: { x: cx - rx * 0.78, y: cy - ry * 1.02 },
  };
}

/** 耳の先端の高さ。耳の形と大きさで変わる。 */
function earTopY(avatar: Avatar, head: Head): number {
  const baseY = head.cy - head.ry * 0.72;
  if (avatar.ears === "folded") return baseY - 8;
  if (avatar.ears === "round") return baseY - 9;
  return baseY - 12;
}

/**
 * 描くものの外接矩形。
 *
 * 顔だけを描く (胴体は描かない) ため、これを 64x64 に合わせて拡大して余白を詰める。
 * アクセサリのはみ出しも数えるので、帽子やヘッドホンを付けた分だけ顔が小さくなる。
 */
function contentBox(avatar: Avatar, head: Head) {
  const { cx, cy, rx, ry } = head;
  const a = anchors(head);
  let top = Math.min(earTopY(avatar, head), cy - ry);
  let bottom = cy + ry;
  let left = cx - rx;
  let right = cx + rx;

  switch (avatar.accessory) {
    case "scarf":
      bottom = Math.max(bottom, a.neckY + 10);
      break;
    case "collar-bell":
      bottom = Math.max(bottom, a.neckY + 10);
      break;
    case "glasses":
      left = Math.min(left, cx - rx * 1.04);
      right = Math.max(right, cx + rx * 1.04);
      break;
    case "headphones":
      left = Math.min(left, cx - rx * 1.14);
      right = Math.max(right, cx + rx * 1.14);
      top = Math.min(top, cy - ry * 1.175 - 2);
      break;
    case "flower":
      top = Math.min(top, a.flower.y - 5.6);
      left = Math.min(left, a.flower.x - 5.6);
      break;
    case "cap":
      top = Math.min(top, a.brimY - ry * 0.62 - 1.4);
      break;
    case "beanie":
      top = Math.min(top, a.brimY + 1.6 - ry * 0.78 - 4.6);
      break;
    case "ribbon":
      top = Math.min(top, a.knot.y - 3.4);
      right = Math.max(right, a.knot.x + 8);
      break;
    case "none":
      break;
  }

  return { top, bottom, left, right };
}

/** 中身を 64x64 の枠に収める transform (余白は左右上下 1.5)。 */
function fitTransform(avatar: Avatar, head: Head): string {
  const box = contentBox(avatar, head);
  const margin = 1.5;
  const scale = Math.min(
    (VIEW_BOX_SIZE - margin * 2) / (box.right - box.left),
    (VIEW_BOX_SIZE - margin * 2) / (box.bottom - box.top),
  );
  const centerX = (box.left + box.right) / 2;
  const centerY = (box.top + box.bottom) / 2;
  const half = VIEW_BOX_SIZE / 2;
  return `translate(${r(half - centerX * scale)} ${r(half - centerY * scale)}) scale(${r(scale)})`;
}

export function avatarDrawing(avatar: Avatar, options?: { idPrefix?: string }): AvatarDrawing {
  const head = HEADS[avatar.shape];
  const fur = FUR_COLORS[avatar.furColor];
  const background = BACKGROUND_COLORS[avatar.background];
  // clip の形は shape だけで決まるので、同じ形の avatar 同士は同じ id を共有してよい
  const clipId = `${options?.idPrefix ?? "talkers-cat"}-clip-${avatar.shape}`;

  const nodes: SvgNode[] = [
    {
      tag: "defs",
      attrs: {},
      children: [
        {
          tag: "clipPath",
          attrs: { id: clipId },
          children: [ellipse(head.cx, head.cy, head.rx, head.ry, "#000")],
        },
      ],
    },
  ];

  if (avatar.background !== "none") {
    nodes.push(circle(32, 32, 32, background.base));
  }

  // 顔だけを描き、枠に合わせて拡大する
  nodes.push({
    tag: "g",
    attrs: { transform: fitTransform(avatar, head) },
    children: [
      ...ears(avatar, head, fur),
      // 白系の毛色が白背景に溶けないよう、輪郭に影色の細い線を入れる
      ellipse(head.cx, head.cy, head.rx, head.ry, fur.base, {
        stroke: fur.shade,
        "stroke-width": 0.9,
      }),
      ...pattern(avatar, head, fur, clipId),
      ...eyes(avatar, head, fur),
      ...muzzle(avatar, head, fur),
      ...whiskers(avatar, head, fur),
      ...accessory(avatar, head),
    ],
  });

  return { viewBox: `0 0 ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`, nodes };
}

function ears(avatar: Avatar, head: Head, fur: Fur): SvgNode[] {
  const nodes: SvgNode[] = [];
  const baseY = head.cy - head.ry * 0.72;

  for (const side of [-1, 1] as const) {
    const x = mirror(head, side);
    const outerBase = head.rx * 0.95;
    const inner = head.rx * 0.1;

    if (avatar.ears === "folded") {
      nodes.push(
        // 立ち上がりは低く、外側に張り出す
        fill(
          `M ${x(head.rx * 0.95)} ${r(baseY + 5)} Q ${x(head.rx * 1.0)} ${r(baseY - 6)} ${x(head.rx * 0.62)} ${r(baseY - 7.5)} Q ${x(head.rx * 0.3)} ${r(baseY - 7)} ${x(head.rx * 0.16)} ${r(baseY + 2)} Z`,
          fur.base,
        ),
        // 上半分が前に倒れて垂れ下がる。折り目から先を影の色にして、折れているのを見せる
        fill(
          `M ${x(head.rx * 0.2)} ${r(baseY - 4)} Q ${x(head.rx * 0.62)} ${r(baseY - 8.5)} ${x(head.rx * 0.98)} ${r(baseY - 3.5)} Q ${x(head.rx * 0.86)} ${r(baseY + 1.5)} ${x(head.rx * 0.5)} ${r(baseY + 1)} Q ${x(head.rx * 0.3)} ${r(baseY + 0.4)} ${x(head.rx * 0.2)} ${r(baseY - 4)} Z`,
          fur.shade,
        ),
        // 折り目
        stroke(
          `M ${x(head.rx * 0.24)} ${r(baseY - 4)} Q ${x(head.rx * 0.62)} ${r(baseY - 8)} ${x(head.rx * 0.94)} ${r(baseY - 3.6)}`,
          fur.base,
          0.9,
        ),
      );
      continue;
    }

    if (avatar.ears === "round") {
      nodes.push(
        fill(
          `M ${x(outerBase)} ${r(baseY + 6)} Q ${x(outerBase)} ${r(baseY - 9)} ${x(head.rx * 0.4)} ${r(baseY - 8)} Q ${x(inner)} ${r(baseY - 5)} ${x(inner)} ${r(baseY + 3)} Z`,
          fur.base,
        ),
        fill(
          `M ${x(outerBase * 0.82)} ${r(baseY + 4)} Q ${x(outerBase * 0.8)} ${r(baseY - 5.5)} ${x(head.rx * 0.42)} ${r(baseY - 5)} Q ${x(head.rx * 0.24)} ${r(baseY - 3)} ${x(head.rx * 0.24)} ${r(baseY + 2)} Z`,
          SKIN,
        ),
      );
      continue;
    }

    // pointed
    nodes.push(
      fill(
        `M ${x(outerBase)} ${r(baseY + 6)} L ${x(head.rx * 0.55)} ${r(baseY - 12)} L ${x(inner)} ${r(baseY + 2)} Z`,
        fur.base,
      ),
      fill(
        `M ${x(outerBase * 0.8)} ${r(baseY + 4)} L ${x(head.rx * 0.55)} ${r(baseY - 7.5)} L ${x(head.rx * 0.24)} ${r(baseY + 1)} Z`,
        SKIN,
      ),
    );
  }
  return nodes;
}

function pattern(avatar: Avatar, head: Head, fur: Fur, clipId: string): SvgNode[] {
  if (avatar.pattern === "solid") return [];

  const children: SvgNode[] = [];
  const { cx, cy, rx, ry } = head;

  if (avatar.pattern === "tabby") {
    for (const i of [-1, 0, 1]) {
      children.push(
        stroke(
          `M ${r(cx + i * 5)} ${r(cy - ry * 0.98)} L ${r(cx + i * 3.4)} ${r(cy - ry * 0.42)}`,
          fur.shade,
          2.2,
        ),
      );
    }
    for (const side of [-1, 1] as const) {
      for (const j of [0, 1]) {
        children.push(
          stroke(
            `M ${r(cx + side * rx * 0.58)} ${r(cy + ry * 0.06 + j * 3.6)} L ${r(cx + side * rx * 1.02)} ${r(cy - ry * 0.04 + j * 3.6)}`,
            fur.shade,
            1.8,
          ),
        );
      }
    }
  }

  if (avatar.pattern === "tuxedo") {
    children.push(
      ellipse(cx, cy + ry * 0.5, rx * 0.74, ry * 0.62, FUR_COLORS.white.base),
      // 額から鼻筋に白が抜ける。ここが「ハチワレ」の由来なので、口まわりだけ白くしても足りない。
      // 額で広がり鼻筋で締まる形にしないと、ただの白い帯に見える
      fill(
        `M ${r(cx - 5)} ${r(cy - ry * 1.05)} Q ${r(cx - 1.7)} ${r(cy - ry * 0.36)} ${r(cx - 1.9)} ${r(cy + ry * 0.32)} L ${r(cx + 1.9)} ${r(cy + ry * 0.32)} Q ${r(cx + 1.7)} ${r(cy - ry * 0.36)} ${r(cx + 5)} ${r(cy - ry * 1.05)} Z`,
        FUR_COLORS.white.base,
      ),
    );
  }

  if (avatar.pattern === "calico") {
    // 色は額から上に乗せる。目の高さに置くと目が潰れて顔が読めなくなる
    children.push(
      ellipse(cx - rx * 0.44, cy - ry * 0.74, 7, 5.4, FUR_COLORS.ginger.base),
      ellipse(cx + rx * 0.5, cy - ry * 0.68, 6, 4.8, FUR_COLORS.black.base, { opacity: 0.85 }),
      ellipse(cx, cy + ry * 0.62, rx * 0.6, ry * 0.45, FUR_COLORS.white.base),
    );
  }

  if (avatar.pattern === "point") {
    // シャム猫の色。顔の下半分を一様に暗くするのではなく、マズルと目のまわりに置く
    children.push(
      ellipse(cx, cy + ry * 0.48, rx * 0.5, ry * 0.38, fur.shade, { opacity: 0.68 }),
      ellipse(cx, cy + ry * 0.02, rx * 0.78, ry * 0.24, fur.shade, { opacity: 0.34 }),
    );
  }

  if (avatar.pattern === "spotted") {
    // 大きさをばらつかせ、顔の片側に寄らないよう散らす。目の高さは避ける
    const spots: [number, number, number][] = [
      [-0.55, -0.62, 2.5],
      [0.5, -0.7, 2.1],
      [-0.72, 0.12, 2.4],
      [0.68, 0.06, 2.0],
      [0.05, -0.86, 2.7],
      [-0.3, -0.44, 1.7],
      [0.34, -0.46, 1.9],
      [-0.6, 0.5, 1.8],
      [0.58, 0.46, 2.1],
    ];
    for (const [dx, dy, radius] of spots) {
      children.push(circle(cx + rx * dx, cy + ry * dy, radius, fur.shade));
    }
  }

  return [{ tag: "g", attrs: { "clip-path": `url(#${clipId})` }, children }];
}

/** odd は左右で別の色にするため、虹彩の色は目ごとに決める。 */
function irisOf(avatar: Avatar, side: -1 | 1) {
  if (avatar.eyeColor === "odd") return side === -1 ? EYE_COLORS.blue : EYE_COLORS.amber;
  return EYE_COLORS[avatar.eyeColor];
}

function eyes(avatar: Avatar, head: Head, fur: Fur): SvgNode[] {
  const nodes: SvgNode[] = [];
  const cy = head.cy + head.ry * 0.02;
  const dx = head.rx * 0.44;

  for (const side of [-1, 1] as const) {
    const cx = head.cx + side * dx;
    const iris = irisOf(avatar, side);
    const closed = avatar.eyes === "closed" || (avatar.eyes === "wink" && side === 1);

    if (closed) {
      nodes.push(
        stroke(
          `M ${r(cx - 3.6)} ${r(cy + 1.4)} Q ${r(cx)} ${r(cy - 2.6)} ${r(cx + 3.6)} ${r(cy + 1.4)}`,
          INK,
          1.4,
        ),
      );
      continue;
    }

    if (avatar.eyes === "sleepy") {
      const w = 3.9;
      const h = 3;
      nodes.push(
        // 半分だけ開いた目。虹彩を描いてから上まぶたで隠す
        ellipse(cx, cy + 0.6, w, h, iris.base),
        ellipse(cx, cy + 0.6, w, h, "none", { stroke: iris.shade, "stroke-width": 0.9 }),
        // 瞳孔は下寄りに小さく置く。まぶたで隠れる分、虹彩の色が見える余地を残す
        ellipse(cx, cy + 1.2, 1.1, 1.7, INK),
        circle(cx - 1.2, cy + 0.4, 0.6, HIGHLIGHT),
        // 上まぶたは半分まで。目の中心より下まで覆うと、眠そうではなく不機嫌な顔になる
        fill(
          `M ${r(cx - w - 0.6)} ${r(cy - h - 0.6)} L ${r(cx + w + 0.6)} ${r(cy - h - 0.6)} L ${r(cx + w + 0.6)} ${r(cy - 1.2)} Q ${r(cx)} ${r(cy + 0.6)} ${r(cx - w - 0.6)} ${r(cy - 1.2)} Z`,
          fur.base,
        ),
        // まぶたの縁
        stroke(
          `M ${r(cx - w)} ${r(cy - 1.3)} Q ${r(cx)} ${r(cy + 0.5)} ${r(cx + w)} ${r(cy - 1.3)}`,
          INK,
          0.9,
        ),
      );
      continue;
    }

    if (avatar.eyes === "almond") {
      nodes.push(
        ellipse(cx, cy, 4, 2.9, iris.base),
        ellipse(cx, cy, 4, 2.9, "none", { stroke: iris.shade, "stroke-width": 0.9 }),
        ellipse(cx, cy, 1.15, 2.3, INK),
        circle(cx - 1.2, cy - 1.1, 0.85, HIGHLIGHT),
      );
      continue;
    }

    const radius = 3.5;
    nodes.push(
      circle(cx, cy, radius, iris.base),
      // 虹彩の外周 (limbal ring) を濃くすると生き物らしく見える
      circle(cx, cy, radius, "none", { stroke: iris.shade, "stroke-width": 1 }),
      ellipse(cx, cy, 1.2, 2.7, INK),
    );

    if (avatar.eyes === "starry") {
      // 星は瞳の上に大きく重ねる。虹彩と同じ大きさだと、ただ白く抜けたようにしか見えない
      const star = (scale: number, color: string, extra?: Record<string, string | number>) => {
        const a = 4.4 * scale;
        const b = 1.15 * scale;
        return fill(
          `M ${r(cx)} ${r(cy - a)} Q ${r(cx + b * 0.5)} ${r(cy - b)} ${r(cx + b)} ${r(cy - b)} Q ${r(cx + b)} ${r(cy - b * 0.5)} ${r(cx + a)} ${r(cy)} Q ${r(cx + b)} ${r(cy + b * 0.5)} ${r(cx + b)} ${r(cy + b)} Q ${r(cx + b * 0.5)} ${r(cy + b)} ${r(cx)} ${r(cy + a)} Q ${r(cx - b * 0.5)} ${r(cy + b)} ${r(cx - b)} ${r(cy + b)} Q ${r(cx - b)} ${r(cy + b * 0.5)} ${r(cx - a)} ${r(cy)} Q ${r(cx - b)} ${r(cy - b * 0.5)} ${r(cx - b)} ${r(cy - b)} Q ${r(cx - b * 0.5)} ${r(cy - b)} ${r(cx)} ${r(cy - a)} Z`,
          color,
          extra,
        );
      };
      nodes.push(
        star(1, iris.shade, { opacity: 0.9 }),
        star(0.78, HIGHLIGHT),
        // 小さい輝きを添えて、目がきらめいて見えるようにする
        circle(cx + 2.6, cy - 2.4, 0.9, HIGHLIGHT, { opacity: 0.9 }),
        circle(cx - 2.9, cy + 2.2, 0.6, HIGHLIGHT, { opacity: 0.75 }),
      );
    } else {
      nodes.push(circle(cx - 1.2, cy - 1.2, 0.9, HIGHLIGHT));
    }
  }
  return nodes;
}

/** 鼻と口。 */
function muzzle(avatar: Avatar, head: Head, fur: Fur): SvgNode[] {
  const { cx, cy, ry } = head;
  // 鼻は元々小さいので、大中小の差が分かるよう倍率を強調する
  const noseY = cy + ry * 0.42;
  const mouthY = noseY + 2.4;
  const nodes: SvgNode[] = [
    fill(
      `M ${r(cx - 2.3)} ${r(noseY)} Q ${r(cx)} ${r(noseY - 0.9)} ${r(cx + 2.3)} ${r(noseY)} Q ${r(cx)} ${r(noseY + 2.6)} ${r(cx - 2.3)} ${r(noseY)} Z`,
      SKIN_DARK,
    ),
  ];

  if (avatar.mouth === "neutral") {
    nodes.push(
      // 鼻から下りる線がないと、横棒 1 本では口があること自体が読めない
      stroke(`M ${r(cx)} ${r(noseY + 1.1)} L ${r(cx)} ${r(mouthY + 0.4)}`, fur.shade, 1.1),
      stroke(
        `M ${r(cx - 3.4)} ${r(mouthY + 0.9)} L ${r(cx + 3.4)} ${r(mouthY + 0.9)}`,
        fur.shade,
        1.3,
      ),
    );
    return nodes;
  }

  if (avatar.mouth === "grumpy") {
    nodes.push(
      stroke(`M ${r(cx)} ${r(noseY + 1.1)} L ${r(cx)} ${r(mouthY - 0.2)}`, fur.shade, 1.1),
      // 中央を持ち上げて両端を下げた「へ」の字。左右の弧だと笑顔との差が出ない
      stroke(
        `M ${r(cx - 4.6)} ${r(mouthY + 2.4)} Q ${r(cx)} ${r(mouthY - 1.6)} ${r(cx + 4.6)} ${r(mouthY + 2.4)}`,
        fur.shade,
        1.4,
      ),
    );
    return nodes;
  }

  if (avatar.mouth === "tongue") {
    // 舌を出しているだけなので口は小さいまま (あーんとは別サイズ)
    for (const side of [-1, 1] as const) {
      nodes.push(
        stroke(
          `M ${r(cx)} ${r(mouthY)} Q ${r(cx + side * 2.4)} ${r(mouthY + 2.6)} ${r(cx + side * 4.8)} ${r(mouthY + 0.4)}`,
          fur.shade,
          1.3,
        ),
      );
    }
    nodes.push(
      ellipse(cx, mouthY + 2.4, 2.6, 2.2, MOUTH_IN),
      ellipse(cx, mouthY + 3.4, 1.7, 1.3, SKIN),
    );
    return nodes;
  }

  if (avatar.mouth === "open") {
    // 縦に開けた口。細長いと舌を出した板に見えるので、幅を持たせて浅くする
    const top = mouthY - 0.4;
    const width = 4.1;
    const depth = 5.4;
    nodes.push(
      fill(
        `M ${r(cx - width)} ${r(top)} Q ${r(cx)} ${r(top - 1.6)} ${r(cx + width)} ${r(top)} Q ${r(cx + width * 0.95)} ${r(top + depth)} ${r(cx)} ${r(top + depth)} Q ${r(cx - width * 0.95)} ${r(top + depth)} ${r(cx - width)} ${r(top)} Z`,
        MOUTH_IN,
      ),
      // 下あごに舌をのぞかせる
      ellipse(cx, top + depth * 0.74, width * 0.52, depth * 0.28, SKIN),
      // 口角
      stroke(
        `M ${r(cx - width - 1.6)} ${r(top - 0.6)} Q ${r(cx - width * 0.5)} ${r(top + 0.6)} ${r(cx - width * 0.1)} ${r(top - 0.2)}`,
        fur.shade,
        1.1,
      ),
      stroke(
        `M ${r(cx + width + 1.6)} ${r(top - 0.6)} Q ${r(cx + width * 0.5)} ${r(top + 0.6)} ${r(cx + width * 0.1)} ${r(top - 0.2)}`,
        fur.shade,
        1.1,
      ),
    );
    return nodes;
  }

  // smile
  for (const side of [-1, 1] as const) {
    nodes.push(
      stroke(
        `M ${r(cx)} ${r(mouthY)} Q ${r(cx + side * 2.4)} ${r(mouthY + 2.6)} ${r(cx + side * 4.8)} ${r(mouthY + 0.4)}`,
        fur.shade,
        1.3,
      ),
    );
  }
  return nodes;
}

/**
 * ひげの形。長さではなく垂れ方で分ける。
 * `spread` は上下への広がり、`sag` は先端が落ちる量、`length` は伸びる長さ。
 */
const WHISKER_SHAPES = {
  straight: { length: 9, spread: 2.0, sag: 0 },
  gentle: { length: 9, spread: 2.4, sag: 2.2 },
  droopy: { length: 11.5, spread: 1.6, sag: 6 },
} as const satisfies Record<WhiskerId, { length: number; spread: number; sag: number }>;

function whiskers(avatar: Avatar, head: Head, fur: Fur): SvgNode[] {
  const { length, spread, sag } = WHISKER_SHAPES[avatar.whiskers];
  const nodes: SvgNode[] = [];
  const baseY = head.cy + head.ry * 0.44;

  for (const side of [-1, 1] as const) {
    for (const i of [-1, 0, 1]) {
      const startX = head.cx + side * head.rx * 0.42;
      const y = baseY + i * 2.4;
      // 上の 1 本は上向き、下の 1 本は下向きに開く。sag はどれも先端を下げる
      const rise = i * spread;
      nodes.push(
        stroke(
          `M ${r(startX)} ${r(y)} Q ${r(startX + side * length * 0.55)} ${r(y + rise * 0.4 + sag * 0.25)} ${r(startX + side * length)} ${r(y + rise + sag)}`,
          fur.shade,
          0.9,
          { opacity: 0.85 },
        ),
      );
    }
  }
  return nodes;
}

function accessory(avatar: Avatar, head: Head): SvgNode[] {
  const { cx, cy, rx, ry } = head;
  const { neckY, eyeY, eyeDx, brimY, knot, flower } = anchors(head);

  switch (avatar.accessory) {
    case "none":
      return [];

    case "scarf": {
      const hangX = cx + rx * 0.24;
      const hangTop = neckY + 4.4;
      return [
        rect(cx - rx * 0.86, neckY, rx * 1.72, 6, 3, ACCENT.scarf),
        // 巻き重ねの折り目
        stroke(
          `M ${r(cx - rx * 0.34)} ${r(neckY + 1.3)} Q ${r(cx)} ${r(neckY + 4.4)} ${r(cx + rx * 0.34)} ${r(neckY + 1.3)}`,
          ACCENT.scarfDark,
          1.3,
        ),
        // 垂れ (顔だけの構図なので短く)
        rect(hangX, hangTop, 5, 5.4, 2.3, ACCENT.scarf),
        stroke(
          `M ${r(hangX + 2.5)} ${r(hangTop + 1.2)} L ${r(hangX + 2.5)} ${r(hangTop + 4.2)}`,
          ACCENT.scarfDark,
          0.8,
          { opacity: 0.8 },
        ),
      ];
    }

    case "collar-bell": {
      const bandY = neckY + 0.4;
      const bellY = bandY + 6;
      return [
        rect(cx - rx * 0.78, bandY, rx * 1.56, 3.8, 1.9, ACCENT.collar),
        // 縫い目のハイライト
        stroke(
          `M ${r(cx - rx * 0.7)} ${r(bandY + 1.2)} L ${r(cx + rx * 0.7)} ${r(bandY + 1.2)}`,
          HIGHLIGHT,
          0.7,
          { opacity: 0.35 },
        ),
        // 鈴を吊る輪
        circle(cx, bandY + 4.2, 1.1, "none", { stroke: ACCENT.metal, "stroke-width": 0.8 }),
        circle(cx, bellY, 3, ACCENT.bell, { stroke: ACCENT.bellDark, "stroke-width": 0.6 }),
        stroke(
          `M ${r(cx - 1.7)} ${r(bellY + 0.7)} L ${r(cx + 1.7)} ${r(bellY + 0.7)}`,
          ACCENT.bellDark,
          0.9,
        ),
        circle(cx, bellY + 1.9, 0.6, ACCENT.bellDark),
        circle(cx - 1, bellY - 1, 0.7, HIGHLIGHT, { opacity: 0.7 }),
      ];
    }

    case "glasses": {
      const lensW = 11.2;
      const lensH = 9.2;
      return [
        rect(cx - eyeDx - lensW / 2, eyeY - lensH / 2, lensW, lensH, 3.4, ACCENT.lens, {
          opacity: 0.16,
        }),
        rect(cx - eyeDx - lensW / 2, eyeY - lensH / 2, lensW, lensH, 3.4, "none", {
          stroke: ACCENT.gear,
          "stroke-width": 1.4,
        }),
        rect(cx + eyeDx - lensW / 2, eyeY - lensH / 2, lensW, lensH, 3.4, ACCENT.lens, {
          opacity: 0.16,
        }),
        rect(cx + eyeDx - lensW / 2, eyeY - lensH / 2, lensW, lensH, 3.4, "none", {
          stroke: ACCENT.gear,
          "stroke-width": 1.4,
        }),
        stroke(
          `M ${r(cx - eyeDx + lensW / 2)} ${r(eyeY - 1)} Q ${r(cx)} ${r(eyeY - 2.8)} ${r(cx + eyeDx - lensW / 2)} ${r(eyeY - 1)}`,
          ACCENT.gear,
          1.4,
        ),
        stroke(
          `M ${r(cx - eyeDx - lensW / 2)} ${r(eyeY - 1.4)} L ${r(cx - rx * 1.02)} ${r(eyeY - 3)}`,
          ACCENT.gear,
          1.4,
        ),
        stroke(
          `M ${r(cx + eyeDx + lensW / 2)} ${r(eyeY - 1.4)} L ${r(cx + rx * 1.02)} ${r(eyeY - 3)}`,
          ACCENT.gear,
          1.4,
        ),
      ];
    }

    case "headphones": {
      const cupW = 6.4;
      const cupH = 11;
      const cupY = cy - ry * 0.44;
      /** カップを外側に倒して、顔の側面のふくらみに沿わせる。 */
      const tilt = 13;
      return [
        // アーチ。頭のてっぺんに沿う高さにする (高く張ると顔から浮いて見える)
        stroke(
          `M ${r(cx - rx * 1.0)} ${r(cy - ry * 0.26)} Q ${r(cx)} ${r(cy - ry * 1.58)} ${r(cx + rx * 1.0)} ${r(cy - ry * 0.26)}`,
          ACCENT.gear,
          3.6,
        ),
        stroke(
          `M ${r(cx - rx * 0.92)} ${r(cy - ry * 0.3)} Q ${r(cx)} ${r(cy - ry * 1.44)} ${r(cx + rx * 0.92)} ${r(cy - ry * 0.3)}`,
          ACCENT.metal,
          1.1,
          { opacity: 0.8 },
        ),
        ...[-1, 1].flatMap((side) => {
          const x = side === -1 ? cx - rx * 1.12 : cx + rx * 1.12 - cupW;
          const midX = x + cupW / 2;
          const midY = cupY + cupH / 2;
          // 左のカップは上を右へ、右のカップは上を左へ倒す
          const spin = { transform: `rotate(${r(-side * tilt)} ${r(midX)} ${r(midY)})` };
          return [
            rect(x, cupY, cupW, cupH, 3.2, ACCENT.gear, spin),
            ellipse(midX, midY, 1.9, 3.6, ACCENT.metal, spin),
          ];
        }),
      ];
    }

    case "flower": {
      const fx = flower.x;
      const fy = flower.y;
      const petals: SvgNode[] = [0, 1, 2, 3, 4].map((i) => {
        const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
        const px = fx + Math.cos(angle) * 3;
        const py = fy + Math.sin(angle) * 3;
        return ellipse(px, py, 2.6, 2, ACCENT.petal, {
          transform: `rotate(${r((360 * i) / 5)} ${r(px)} ${r(py)})`,
        });
      });
      return [
        ellipse(fx + 3.6, fy + 3, 2.6, 1.3, ACCENT.leaf, {
          transform: `rotate(28 ${r(fx + 3.6)} ${r(fy + 3)})`,
        }),
        ...petals,
        circle(fx, fy, 1.9, ACCENT.pollen),
        circle(fx, fy, 1.9, "none", { stroke: ACCENT.petalDark, "stroke-width": 0.5 }),
      ];
    }

    case "cap": {
      // 正面から見た野球帽。つばは左右ではなく手前に張り出させないと、ただの椀に見える
      const domeTop = brimY - ry * 0.62;
      const edge = rx * 0.86;
      return [
        fill(
          `M ${r(cx - edge)} ${r(brimY + 0.8)} A ${r(edge)} ${r(ry * 0.66)} 0 0 1 ${r(cx + edge)} ${r(brimY + 0.8)} Z`,
          ACCENT.cap,
        ),
        // 縫い目
        stroke(
          `M ${r(cx)} ${r(domeTop + 0.8)} Q ${r(cx + rx * 0.24)} ${r(brimY - ry * 0.28)} ${r(cx + rx * 0.34)} ${r(brimY + 0.6)}`,
          ACCENT.capBrim,
          0.8,
          { opacity: 0.8 },
        ),
        // つば。手前に垂れる半月
        fill(
          `M ${r(cx - edge - 0.8)} ${r(brimY + 0.6)} Q ${r(cx)} ${r(brimY + 9)} ${r(cx + edge + 0.8)} ${r(brimY + 0.6)} Q ${r(cx)} ${r(brimY + 3.4)} ${r(cx - edge - 0.8)} ${r(brimY + 0.6)} Z`,
          ACCENT.capBrim,
        ),
        circle(cx, domeTop + 0.6, 1.3, ACCENT.capBrim),
      ];
    }

    case "beanie": {
      // ニット帽。深くかぶせて、折り返しの縁とぼんぼんで帽子と分かるようにする
      const cuffY = brimY + 1.6;
      const edge = rx * 0.9;
      const domeTop = cuffY - ry * 0.78;
      return [
        fill(
          `M ${r(cx - edge)} ${r(cuffY)} A ${r(edge)} ${r(ry * 0.78)} 0 0 1 ${r(cx + edge)} ${r(cuffY)} Z`,
          ACCENT.beanie,
        ),
        // 編み目
        ...[-0.5, 0, 0.5].map((t) =>
          stroke(
            `M ${r(cx + edge * t * 1.2)} ${r(cuffY - 0.4)} Q ${r(cx + edge * t * 1.05)} ${r(domeTop + ry * 0.3)} ${r(cx + edge * t * 0.5)} ${r(domeTop + 1)}`,
            ACCENT.beanieCuff,
            0.7,
            { opacity: 0.55 },
          ),
        ),
        // 折り返し
        rect(cx - edge - 0.6, cuffY - 1.4, (edge + 0.6) * 2, 3.6, 1.6, ACCENT.beanieCuff),
        // ぼんぼん
        circle(cx, domeTop - 1.4, 3, ACCENT.beanieCuff),
        circle(cx - 0.8, domeTop - 2.2, 1.1, ACCENT.beanie, { opacity: 0.5 }),
      ];
    }

    case "ribbon": {
      const kx = knot.x;
      const ky = knot.y;
      const loop = (side: -1 | 1) =>
        fill(
          `M ${r(kx)} ${r(ky)} Q ${r(kx + side * 8)} ${r(ky - 4.6)} ${r(kx + side * 7)} ${r(ky + 1.2)} Q ${r(kx + side * 6.4)} ${r(ky + 4)} ${r(kx)} ${r(ky + 0.6)} Z`,
          ACCENT.ribbon,
        );
      return [
        loop(-1),
        loop(1),
        stroke(
          `M ${r(kx)} ${r(ky + 0.6)} Q ${r(kx - 1.6)} ${r(ky + 4.4)} ${r(kx - 3.4)} ${r(ky + 6)}`,
          ACCENT.ribbonDark,
          1.5,
        ),
        stroke(
          `M ${r(kx)} ${r(ky + 0.6)} Q ${r(kx + 1.6)} ${r(ky + 4.4)} ${r(kx + 3.4)} ${r(ky + 6)}`,
          ACCENT.ribbonDark,
          1.5,
        ),
        circle(kx, ky + 0.3, 1.8, ACCENT.ribbonDark),
      ];
    }
  }
}

const escapeAttr = (value: string | number): string =>
  String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

function serialize(node: SvgNode): string {
  const attrs = Object.entries(node.attrs)
    .map(([key, value]) => ` ${key}="${escapeAttr(value)}"`)
    .join("");
  const children = (node.children ?? []).map(serialize).join("");
  return children === ""
    ? `<${node.tag}${attrs}/>`
    : `<${node.tag}${attrs}>${children}</${node.tag}>`;
}

/** 単体の SVG 文字列。CLI やファイル書き出し用。 */
export function avatarSvg(avatar: Avatar, options?: { size?: number; idPrefix?: string }): string {
  const drawing = avatarDrawing(avatar, options);
  const size = options?.size ?? VIEW_BOX_SIZE;
  const body = drawing.nodes.map(serialize).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="${drawing.viewBox}" role="img">${body}</svg>`;
}
